import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBilling } from "@/context/BillingContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const DeleteAccountCard = () => {
  const { logout } = useAuth();
  const { plan } = useBilling();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const ready = password.length > 0 && confirm.trim().toUpperCase() === "DELETE";

  const submit = async (e) => {
    e.preventDefault();
    if (!ready || busy) return;
    setBusy(true);
    try {
      const r = await api.post("/auth/delete-account", { password, confirm: confirm.trim() });
      toast.success(r.data.subscriptions_cancelled ? "Account deleted and subscription cancelled" : "Your account has been deleted");
      setOpen(false);
      await logout();
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(apiError(err, "Could not delete account"));
      setBusy(false);
    }
  };

  return (
    <div className="apx-card p-5 border border-rose-900/50" data-testid="danger-zone-card">
      <div className="font-display font-semibold text-rose-300">Danger zone</div>
      <p className="text-xs text-slate-400 mt-1">
        Permanently delete your EV-IDEX account and all data linked to it: profile, simulated portfolio and trades, watchlist, alerts, journal, notifications, chat history, learning progress and phone-notification devices.
        {plan !== "free" && <strong className="text-slate-300"> Your {plan} subscription will be cancelled immediately (no further charges).</strong>}
        {" "}This cannot be undone. <Link to="/delete-account" className="text-emerald-400 hover:underline" data-testid="delete-account-info-link">What gets deleted?</Link>
      </p>
      <button data-testid="open-delete-account-btn" onClick={() => setOpen(true)} className="mt-4 inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium border border-rose-700/60 text-rose-300 hover:bg-rose-950/40 transition-colors">
        <Trash2 className="w-4 h-4" /> Delete my account
      </button>

      <Dialog open={open} onOpenChange={(o) => { if (!busy) { setOpen(o); if (!o) { setPassword(""); setConfirm(""); } } }}>
        <DialogContent className="bg-[#0F141C] border-slate-800 text-slate-100 sm:max-w-md" data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-rose-300">Delete account permanently?</DialogTitle>
            <DialogDescription className="text-slate-400">
              All your data is erased right away and you will be signed out. There is no recovery.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <label className="block text-sm">
              <span className="text-slate-400">Your password</span>
              <input data-testid="delete-account-password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="apx-input mt-1 w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2" required />
            </label>
            <label className="block text-sm">
              <span className="text-slate-400">Type <strong className="text-slate-200 font-mono">DELETE</strong> to confirm</span>
              <input data-testid="delete-account-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="apx-input mt-1 w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 font-mono" placeholder="DELETE" required />
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" data-testid="delete-account-cancel" disabled={busy} onClick={() => setOpen(false)} className="btn-ghost rounded-lg px-3 py-2 text-sm">Keep my account</button>
              <button type="submit" data-testid="delete-account-submit" disabled={!ready || busy} className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold bg-rose-600 hover:bg-rose-500 disabled:opacity-40 disabled:cursor-not-allowed text-white transition-colors">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} Delete forever
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
